import type {
  BorderProps,
  Slide,
  TableCell,
  TextProps,
} from '@features/powerpoint';
import { generateTableV2 } from '../../../../../utils/build-table-v2';
import { contentSlideHeaderV2 } from '../../../../../utils/slide-header-v2';
import { themeV2 } from '../../../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../../../utils/constants-v2';
import type { CampaignAnalyticsSlidesV2Props } from './build-campaign-analytics-slides-v2';
import {
  stateVisitRows,
  dmaVisitRows,
} from '../../campaign-analytics/data-transforms';

const NO_BORDER: BorderProps = { type: 'none' };
const BOTTOM_BORDER: BorderProps = {
  type: 'solid',
  color: themeV2.colors.magnetBlue,
  pt: 1,
};
const BOTTOM_ONLY_BORDER: NonNullable<TableCell['options']>['border'] = [
  NO_BORDER,
  NO_BORDER,
  BOTTOM_BORDER,
  NO_BORDER,
];

const TABLE_HEADER_OPTIONS: TableCell['options'] = {
  bold: true,
  color: themeV2.colors.metricBlue,
  fontFace: DEFAULT_FONT,
  border: BOTTOM_ONLY_BORDER,
};

const withNoDividers = (rows: TableCell[][]): TableCell[][] =>
  rows.map((row, rowIndex) =>
    row.map(cell => ({
      ...cell,
      options: {
        ...cell.options,
        border: rowIndex === rows.length - 1 ? NO_BORDER : BOTTOM_ONLY_BORDER,
      },
    }))
  );

const stateVisitColumnLabels: TableCell[] = [
  { text: 'Rank', options: TABLE_HEADER_OPTIONS },
  { text: 'State', options: TABLE_HEADER_OPTIONS },
  { text: 'Visit Index', options: { ...TABLE_HEADER_OPTIONS, align: 'right' } },
];

const dmaVisitColumnLabels: TableCell[] = [
  { text: 'Rank', options: TABLE_HEADER_OPTIONS },
  { text: 'DMA', options: TABLE_HEADER_OPTIONS },
  { text: 'Visit Index', options: { ...TABLE_HEADER_OPTIONS, align: 'right' } },
];

const VISITS_DISCLAIMER = 'Visit Index (Average = 100)';

const subHeaderOptions: TextProps['options'] = {
  fontSize: 10,
  bold: true,
  color: themeV2.colors.metricBlue,
  fontFace: DEFAULT_FONT,
  h: 0.47,
  w: 3.26,
};

export const stateAndDmaVisitsTableSlideV2 = (
  props: CampaignAnalyticsSlidesV2Props
): Slide => {
  const tables = [
    ...(props.campaignAnalyticsReport.stateVisits.visible
      ? [
          generateTableV2(
            stateVisitColumnLabels,
            withNoDividers(
              stateVisitRows(props.campaignAnalyticsReport.stateVisits)
            ),
            {
              rowH: 0.1,
              w: 6,
              colW: [1, 4, 1],
              y: 1.37,
              x: 0.44,
            }
          ),
        ]
      : []),
    ...(props.campaignAnalyticsReport.dmaVisits.visible
      ? [
          generateTableV2(
            dmaVisitColumnLabels,
            withNoDividers(
              dmaVisitRows(props.campaignAnalyticsReport.dmaVisits)
            ),
            {
              rowH: 0.1,
              w: 6,
              colW: [1, 4, 1],
              y: 1.37,
              x: 6.87,
            }
          ),
        ]
      : []),
  ];

  const texts: TextProps[] = [
    contentSlideHeaderV2('Top State & DMA Visits'),
    {
      text: VISITS_DISCLAIMER,
      options: {
        x: 1.59,
        y: 7.15,
        h: 0.24,
        w: 6.67,
        fontSize: 8,
        color: themeV2.colors.metricBlue,
        fontFace: DEFAULT_FONT,
      },
    },
    ...(props.campaignAnalyticsReport.stateVisits.visible
      ? [
          {
            text: 'Top 20 States',
            options: { ...subHeaderOptions, x: 0.44, y: 1 },
          },
        ]
      : []),
    ...(props.campaignAnalyticsReport.dmaVisits.visible
      ? [
          {
            text: 'Top 20 DMAs',
            options: { ...subHeaderOptions, x: 6.87, y: 1 },
          },
        ]
      : []),
  ];

  return {
    slideMaster: props.slideMaster,
    tables,
    texts,
  };
};
